import styled from "@emotion/styled";
import {
  Icon,
  Paper,
  Theme,
  Typography,
  useTheme,
} from "@yuno/design-system-web";
import { useContext, useState } from "react";
import { MainContext } from "../../../context/main-context";
import { PreviewItemList } from "./preview-item-list";
import { Divider } from "../common/divider";
import empty from "../../../assets/empty_state.svg";
import { useTranslation } from "react-i18next";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 56px;
  align-items: center;
  justify-content: flex-start;
  padding-bottom: 40px;
  min-height: 600px;
  overflow-y: auto;
`;

const Container = styled(Paper)<{ theme?: Theme; minSize: boolean }>`
  width: 577px;
  padding-top: 24px;
  padding-bottom: 24px;
  border-radius: 16px;
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: 0px;
  height: ${({ minSize }) => (minSize ? "600px" : "auto")};

  .divider {
    margin-top: 1px;
    margin-bottom: 24px;

    p {
      ${({ theme }) => theme.typography.small.regular};
    }
  }

  &.empty-state {
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    & .empty-title {
      ${({ theme }) => theme.typography.xl.bold};
      margin-top: 40px;
    }

    & .empty-description {
      ${({ theme }) => theme.typography.small.regular};
      padding: 0 130px;
      text-align: center;
      margin-top: 12px;
      color: ${({ theme }) => theme.palette.grey[4]};
    }
  }

  .divider hr {
    border-color: ${({ theme }) => theme.palette.grey[2]};
  }
`;

const Title = styled(Typography)<{ theme?: Theme }>`
  text-align: center;
  ${({ theme }) => theme.typography.l.bold};
  margin-bottom: 24px;
`;

const Alert = styled.div<{ theme?: Theme }>`
  width: 577px;
  justify-content: center;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  position: relative;
  display: flex;
  padding: 10px 24px;
  background: ${({ theme }) => theme.palette.pastel.sunset};
  p {
    margin-left: 10px;
    ${({ theme }) => theme.typography.tiny.regular};
  }

  svg {
    fill: ${({ theme }) => theme.palette.vivid.sunset};
  }
`;

export const PreviewList = () => {
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { lists } = useContext(MainContext);

  const card = lists.enrollment.find(
    (el) => el.payment_method_type === "CARD" && el.is_active
  );

  const enrollmentsSize = lists.enrollment.filter(
    ({ is_active }) => is_active
  ).length;
  const paymentsSize = lists.paymentMethods.filter(
    ({ is_active }) => is_active
  ).length;
  const hasEnrollment = enrollmentsSize > 0;
  const hasPayments = paymentsSize > 0;

  const hasActive = hasEnrollment || hasPayments;
  const minSize = enrollmentsSize + paymentsSize <= 4;

  if (!hasActive) {
    return (
      <Wrapper>
        <Container className="empty-state" minSize={true}>
          <img src={empty} />
          <Typography className="empty-title">{t("emptyTitle2")}</Typography>
          <Typography className="empty-description">
            {t("emptyBody2")}
          </Typography>
        </Container>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Container minSize={minSize}>
        <Title variant="h6">{t('displayOrder')}</Title>

        {lists.enrollment.filter(({ is_active }) => is_active).length > 0 && (
          <PreviewItemList data={lists.enrollment} />
        )}

        {((hasEnrollment && hasPayments) || card) && (
          <Divider>
            <Typography>{t('orPay')}</Typography>
          </Divider>
        )}

        {(lists.paymentMethods.filter(({ is_active }) => is_active).length >
          0 ||
          card) && (
          <PreviewItemList
            data={lists.paymentMethods}
            hasCard={Boolean(card)}
          />
        )}
      </Container>
      <Alert>
        <Icon
          name="WarningCircle"
          weight="fill"
          width="16px"
          height="16px"
          preserveAspectRatio="xMidYMid slice"
        />
        <Typography>{t('footerList')}</Typography>
      </Alert>
    </Wrapper>
  );
};
